import * as React from "react";
import { useFormApi, Option, FormState } from "informed";
import { Link } from "react-router-dom";
import { Role, UserCreation, WebPortalClient } from "webportalclient";
import { useSecurityContext } from "../../Core/SecurityContext";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { TextBox } from "../../forms/informedv2/TextBox";
import { Required } from "../../forms/validation/Required";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { HasGlobalPermission } from "../../misc/components/Authorization";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { Checkbox } from "../../forms/informedv2/Checkbox";
import { EmailValidator } from "../../forms/validation/EmailValidator";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import { GetCountOfObjectKeys, GroupBy, IsArrayNullOrEmpty } from "../../misc/Helpers/ArrayHelper";
import { DeviceTypes } from "../../misc/api/apiTypes";
import { UserMutation } from "webportalclient";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import { RequiredWhen } from "../../forms/validation/RequiredWhen";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export interface UserFormProps {
    header: string;
    userId?: string;
}

export interface UserFormState {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    roles: any[];
    sites: any[];
    timeZone: string;
    country: string;
    spirotracUserGroup: string;
    registeredForMobileApp: boolean;
    isDisabled: boolean;
}

export const UserForm = ({ header, userId }: UserFormProps) => {
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [roles, setRoles] = React.useState([]);
    const [registeredEmails, setRegisteredEmails] = React.useState([]);
    const [roleOptions, setRoleOptions] = React.useState([]);
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [timeZoneOptions, setTimeZoneOptions] = React.useState([]);
    const [spirotracUserGroupOptions, setSpirotracUserGroupOptions] = React.useState([]);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const client = useWebPortalApiClient();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { countries } = useReferenceContextProvider();
    const formApi = useFormApi<UserFormState>();
    const isNew = userId === undefined;

    React.useEffect(() => {
        const fetchData = async () => {
            const mapToOptions = (results: { id?: string; name?: string }[]) => results.map(v => ({ label: v.name, value: v.id }));
            const mapToSiteOptions = (results: { id?: string; number?: string }[]) => results.map(v => ({ label: v.number, value: v.id }));
            const mapToRoleOptions = (results: Role[]) => results.map(v => ({ label: `(${v.study?.name ?? "All Studies"}) ${v.name}`, value: v.id }));

            const getAssignableRoles = (roles) => roles.filter(r => r.name !== "Device Hub Client" && r.name !== "Gateway Client");

            const fetchRegisteredUsers = async () => {
                const users = await client.client.getUsers("id,email");
                setRegisteredEmails(users.value);
            }

            const fetchSiteOptions = async () => {
                const sites = await client.client.getSites("id,number", undefined, "number");
                setSiteOptions(mapToSiteOptions(sites.value));
            }

            const fetchRoleOptions = async () => {
                const rolesResults = await client.client.getRoles(undefined, undefined, "name", "study($select=name,deviceTypeIds)");
                setRoles(rolesResults.value);

                if (HasGlobalPermission(currentUserPermissions, PermissionEnum.AssignAllRoles)) {
                    setRoleOptions(mapToRoleOptions(getAssignableRoles(rolesResults.value)));
                } else {
                    const childRoleIds = currentUser.Roles.flatMap(r => r.ChildRolesIds);
                    const childRoles = [];
                    for (const roleId of childRoleIds) {
                        childRoles.push(rolesResults.value.find(r => r.id === roleId));
                    };
                    setRoleOptions(mapToRoleOptions(getAssignableRoles(childRoles)));
                }
            }

            const fetchTimeZoneOptions = async () => {
                const timeZones = await client.client.getTimeZones("id,name", undefined, "sortOrder");
                setTimeZoneOptions(mapToOptions(timeZones.value));
            }

            const fetchSpirotracUserGroupOptions = async () => {
                const spirotracUserGroups = await client.client.getSpirotracUserGroups("id,name", undefined);
                setSpirotracUserGroupOptions(mapToOptions(spirotracUserGroups.value));
            }

            setLoading(true);
            await fetchRegisteredUsers();
            await fetchRoleOptions();
            await fetchSiteOptions();
            await fetchTimeZoneOptions();
            await fetchSpirotracUserGroupOptions();
            setLoading(false);
        }

        fetchData();
    }, []);

    const getSelectedApiRoles = (selectedRoles: any[]) => roles.filter(r => selectedRoles.some(v => v.value === r.id));

    const isPermissionRequired = (selectedRoles: any[], permission: PermissionEnum) => {
        return getSelectedApiRoles(selectedRoles).some(r => r.permissionsIds.includes(permission));
    }

    const isSelectedStudyConfiguredForMobile = (groupedRolesStudies) => {
        Object.keys(groupedRolesStudies).forEach((key, _) => {
            const study = groupedRolesStudies[key];
            study.forEach((_, index) => {
                const role = study[index];
                const deviceTypes = role.study.deviceTypeIds;
                const configured = deviceTypes.includes(DeviceTypes.MobileApp);
                if (configured)
                    return true;
            })
        });

        return false;
    }

    const validateEmail = (value: string) => {
        const requiredMessage = Required(value);
        if (requiredMessage)
            return requiredMessage;

        const invalidMessage = EmailValidator(value);
        if (invalidMessage)
            return invalidMessage;

        if (registeredEmails.filter(e => e.id === userId).includes(value))
            return "Another user is already registered with this email address";

        return undefined;
    }

    const validateRoles = (value: any, values: any) => {
        if (IsArrayNullOrEmpty(value))
            return "Field is required";

        if (values.registeredForMobileApp) {
            const selectedRoles = getSelectedApiRoles(value);
            const rolesGroupedByStudy = GroupBy(selectedRoles, "studyId");

            if ('null' in rolesGroupedByStudy)
                return "A role must be related to a study for mobile application registration";

            if (!isSelectedStudyConfiguredForMobile(rolesGroupedByStudy))
                return "The selected study is not configured to use the mobile app";

            const groupedRolesCount = GetCountOfObjectKeys(rolesGroupedByStudy);
            if (groupedRolesCount !== 1)
                return "A mobile app user must only be a member of a single study";

            if (values.spirotracUserGroup)
                return "A mobile app user cannot be a member of a spirotrac user group";
        }

        return undefined;
    }

    const validateSites = (value: any, values: any) => {
        if (!value || value.length === 0) {
            if (!IsArrayNullOrEmpty(values.roles)) {
                const siteRequired = isPermissionRequired(values.roles, PermissionEnum.RequiresSite);
                if (values.roles.map(r => r.value).includes(roles.find(x => x.name === "Administrator").id)) {
                    return undefined;
                }
                if (siteRequired)
                    return "A site selection is required for this user's role selections";
            }
            if (values.registeredForMobileApp)
                return "A mobile app user must be associated with a single site";
        }
    }

    const validateSpirotracUserGroup = (value: string, values: UserFormState) => {
        if (value && values.registeredForMobileApp)
            return "A mobile app user cannot be a member of a spirotrac user group";
    }

    const validateForm = () => {
        formApi.validate();
        const formState = formApi.getState();
        return formState.errors && Object.keys(formState.errors).length === 0 && Object.getPrototypeOf(formState.errors) === Object.prototype;
    }

    const onPreAuditSubmit = () => {
        setSubmitting(true);

        const valid = validateForm();
        if (!valid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setErrorAlert(null);

        const valid = validateForm();
        if (!valid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const formState = formApi.getState();
        isNew ? await postUser(formState) : await patchUser(formState, auditForm.Annotation);

        setSubmitting(false);
    }

    const postUser = async (formState: FormState<UserFormState>) => {
        const creationModel: UserCreation = {
            email: formState.values.email,
            password: formState.values.password,
            firstName: formState.values.firstName,
            lastName: formState.values.lastName,
            roleIds: formState.values.roles.map(r => r.value),
            countryId: formState.values.country,
            siteIds: formState.values.sites && formState.values.sites.length > 0 ? formState.values.sites.map(r => r.value) : [],
            timeZoneId: formState.values.timeZone,
            spirotracUserGroupId: formState.values.spirotracUserGroup,
            isRegisteredForMobileApp: formState.values.registeredForMobileApp
        }

        try {
            const user = await client.client.postUser(creationModel);
            if (user)
                setSuccessAlert(true);
        } catch (ex) {
            setErrorAlert(ex.message);
            setAuditModalVisible(false);
        }
    }

    const patchUser = async (formState: FormState<UserFormState>, auditAnnotation: string) => {
        const mutationModel: UserMutation = {
            password: formState.values.password,
            roleIds: formState.values.roles.map(r => r.value),
            siteIds: formState.values.sites && formState.values.sites.length > 0 ? formState.values.sites.map(r => r.value) : [],
            countryId: formState.values.country,
            timeZoneId: formState.values.timeZone,
            spirotracUserGroupId: formState.values.spirotracUserGroup,
            isRegisteredForMobileApp: formState.values.registeredForMobileApp,
            isDisabled: formState.values.isDisabled,
        }

        try {
            const userResult = await client.client.patchUser(mutationModel, userId, auditAnnotation);
            if (userResult)
                setSuccessAlert(true);
            setAuditModalVisible(false);
        } catch (ex) {
            setErrorAlert(ex.message);
            setAuditModalVisible(false);
        }
    }

    const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
        return (
            <div className="h2 pull-right">
                <AsyncActionButton className="btn btn-primary" type="submit" isSubmitting={submitting} disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                    Save
                </AsyncActionButton>&nbsp;
                <Link className="btn btn-primary btn-outline" to="/users" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                    Return to List
                </Link>
            </div>
        );
    }

    if (loading)
        return <LoadingWithMessage message="Loading User Form..." />

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/users`}>Users list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary" data-automationid="CreateUserHeader">{header}</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={isNew ? onSubmit : onPreAuditSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="corePanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Core Details</h4>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Email</label>
                                    <TextBox<string, UserFormState>
                                        id="email"
                                        type="email"
                                        field="email"
                                        maxLength={256}
                                        disabled={!isNew}
                                        validate={validateEmail}
                                    />
                                </div>
                                {isNew ? <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Initial Password</label>
                                    <TextBox<string, UserFormState>
                                        id="password"
                                        field="password"
                                        type="password"
                                        validate={(value) => RequiredWhen(value, userId === undefined)}
                                    />
                                </div> : null}
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">First Name</label>
                                    <TextBox<string, UserFormState>
                                        id="firstName"
                                        field="firstName"
                                        validate={isNew ? Required : undefined}
                                        disabled={!isNew}
                                    />
                                </div>
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Last Name</label>
                                    <TextBox<string, UserFormState>
                                        id="lastName"
                                        field="lastName"
                                        validate={isNew ? Required : undefined}
                                        disabled={!isNew}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Web Portal Roles</label>
                                    <MultiSelect
                                        
                                        id="roles"
                                        field="roles"
                                        labelKey="label"
                                        valueKey="value"
                                        multi
                                        options={roleOptions}
                                        validate={validateRoles}
                                    />
                                </div>
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Sites</label>
                                    <MultiSelect
                                        id="sites"
                                        field="sites"
                                        labelKey="label"
                                        valueKey="value"
                                        multi
                                        options={siteOptions}
                                        validate={validateSites}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="timeZone">Preferred Time Zone</label>
                                    <Dropdown id="timeZone" field="timeZone" validate={Required}>
                                        <Option key="OptionBlank" value=""></Option>
                                        {timeZoneOptions.map(cr => (<Option key={`${cr.value}`} value={cr.value}>{cr.label}</Option>))}
                                    </Dropdown>
                                </div>
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="country">Country</label>
                                    <Dropdown id="country" field="country" validate={isNew ? Required : undefined} disabled={!isNew}>
                                        <Option key="OptionBlank" value=""></Option>
                                        {countries.map(cr => (<Option key={`${cr.id}`} value={cr.id}>{cr.name}</Option>))}
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="row">
                                {!isNew ? <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <Checkbox id="isDisabled" field="isDisabled" label="Is Disabled" />
                                </div> : null}
                            </div>
                        </div>
                    </div>
                    <div id="deviceIntegrationPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Device Integration</h4>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="spirotracUserGroup">Spirotrac VI User Group</label>
                                    <Dropdown id="spirotracUserGroup" field="spirotracUserGroup" validate={validateSpirotracUserGroup}>
                                        <Option key="OptionBlank" value=""></Option>
                                        {spirotracUserGroupOptions.map(cr => (<Option key={`${cr.value}`} value={cr.value}>{cr.label}</Option>))}
                                    </Dropdown>
                                </div>
                                {
                                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                        <Checkbox id="registeredForMobileApp" field="registeredForMobileApp" label="User Is Registered For Mobile Spirometry App" />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={isNew ? onSubmit : onPreAuditSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}
